'use client'

import { useState } from 'react'
import Modal from './Modal'
import apiService from '@/app/services/apiService'
import { toast } from 'react-hot-toast'

interface CreateContractModalProps {
  reservationId: string
  isOpen: boolean
  onClose: () => void
  onCreated?: (contract: any) => void
}

export default function CreateContractModal({
  reservationId,
  isOpen,
  onClose,
  onCreated,
}: CreateContractModalProps) {
  const [contractClient, setContractClient] = useState('')
  const [contractTotal, setContractTotal] = useState('')
  const [cpf, setCpf] = useState('')
  const [cnpj, setCnpj] = useState('')
  const [deadline, setDeadline] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async () => {
    setIsSubmitting(true)
    setError('')
    const preparedCpf = cpf.trim() === '' ? null : cpf;
    const preparedCnpj = cnpj.trim() === '' ? null : cnpj;
    try {
      const res = await apiService.postWithToken(
        `/api/reservations/${reservationId}/contract/`,
        {
          contract_client: contractClient || null,
          contract_total: parseFloat(contractTotal),
          cpf: preparedCpf,
          cnpj: preparedCnpj,
          deadline: deadline || null,
        }
      )
      if (onCreated) onCreated(res)
      onClose()
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erro ao criar contrato.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const validateCpf = (cpf: string) => {
    const cpfNumbers = cpf.replace(/\D/g, '');
    return cpfNumbers.length === 11;
  };

  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCpf(value);
  };

  const handleCpfBlur = () => {
    const onlyNumbers = cnpj.replace(/\D/g, '');
    if (onlyNumbers.length === 0) return; // não valida campo vazio

    if (!validateCpf(cpf)) {
      toast.error('CPF inválido. Deve conter 11 dígitos numéricos.');
    }
  };

  const validateCnpj = (cnpj: string) => {
    const cnpjNumbers = cnpj.replace(/\D/g, '');
    return cnpjNumbers.length === 14;
  };

  const handleCnpjChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCnpj(value);
  };

  const handleCnpjBlur = () => {
    const onlyNumbers = cnpj.replace(/\D/g, '');
    if (onlyNumbers.length === 0) return; // não valida campo vazio

    if (!validateCnpj(cnpj)) {
      toast.error('CNPJ inválido. Deve conter 14 dígitos numéricos.');
    }
  };

  return (
    <Modal
      label="Criar Contrato"
      isOpen={isOpen}
      close={onClose}
      content={
        <div className="space-y-4 p-4">
          <div>
            <label className="block text-xs font-medium">Cliente</label>
            <input
              type="text"
              value={contractClient}
              onChange={(e) => setContractClient(e.target.value)}
              className="mt-1 w-full border rounded p-2 text-sm"
              placeholder="Nome do cliente"
            />
          </div>

          <div>
            <label className="block text-xs font-medium">Valor Total (R$)</label>
            <input
              type="number"
              value={contractTotal}
              onChange={(e) => setContractTotal(e.target.value)}
              className="mt-1 w-full border rounded p-2 text-sm"
              placeholder="Ex: 26000.00"
            />
          </div>

          <div>
            <label className="block text-xs font-medium">CPF</label>
            <input
              value={cpf}
              onChange={handleCpfChange}
              onBlur={handleCpfBlur}
              className="mt-1 w-full border rounded p-2 text-sm"
              placeholder="CPF do contratante..."
            />
          </div>

          <div>
            <label className="block text-xs font-medium">CNPJ</label>
            <input
              type="text"
              value={cnpj}
              onChange={handleCnpjChange}
              onBlur={handleCnpjBlur}
              className="mt-1 w-full border rounded p-2 text-sm"
              placeholder="Ex: 12.345.678/0001-90"
            />
          </div>

          <div>
            <label className="block text-xs font-medium">Data do evento</label>
            <input
              type="date"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              className="mt-1 w-full border rounded p-2 text-sm"
            />
          </div>

          {error && <p className="text-red-600 text-sm">{error}</p>}

          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !contractTotal}
            className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded disabled:opacity-50"
          >
            {isSubmitting ? 'Criando...' : 'Criar Contrato'}
          </button>
        </div>
      }
    />
  )
}
